import styled from "styled-components";
import { SettingsSectionTitle, SettingsFormGrid, SettingsFormInput } from "./SettingShell";

export const SectionTitle = styled(SettingsSectionTitle)`
  display: inline-flex;
  align-items: center;
  gap: 11px;
  text-transform: none;
  letter-spacing: -0.01em;
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  &::before {
    content: "";
    width: 4px;
    height: 17px;
    border-radius: 3px;
    background: linear-gradient(to bottom, #3f88a5, #2563eb);
    flex-shrink: 0;
  }
`;

export const RoomyGrid = styled(SettingsFormGrid)`
  gap: 28px 40px;
  margin-top: 10px;
  align-items: start;
`;

export const RoomyInput = styled(SettingsFormInput)`
  padding: 12px 14px;
  font-size: 14px;
  border-radius: 9px;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &:hover:not([readonly]):not(:focus) { border-color: #cbd5e1; }
  &[readonly] {
    cursor: default;
    background: #f8fafc;
    color: #475569;
  }
  &[readonly]:focus { box-shadow: none; border-color: #e2e8f0; }
  ${({ $error }) =>
    $error &&
    `
    border-color: #dc2626;
    &:focus { border-color: #dc2626; box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.12); }
  `}
`;

export const Section = styled.div`
  margin-top: 36px;
  &:first-child { margin-top: 4px; }
`;

export const CheckRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 18px;
`;

export const CheckItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 14px;
  color: #1e293b;
  max-width: 360px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 11px;
  background: #fff;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  transition: border-color 120ms ease, background 120ms ease, box-shadow 120ms ease;
  input { margin-top: 2px; width: 16px; height: 16px; accent-color: #3f88a5; }
  &:hover {
    ${({ $disabled }) =>
      $disabled ? "" : "border-color: #bcd3de; background: #fbfdfe;"}
  }
  &:has(input:checked) {
    border-color: #9fc3d1;
    background: #f4f9fb;
    box-shadow: 0 0 0 1px #9fc3d1 inset;
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 30px;
`;

export const SaveNote = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 30px;
  padding: 12px 15px;
  background: #f1f7fa;
  border: 1px solid #d6e6ee;
  border-radius: 10px;
  font-size: 12.5px;
  color: #396079;
  line-height: 1.55;
  i { margin-top: 1px; color: #3f88a5; }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

export const Dialog = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
`;

export const DialogHead = styled.div`
  padding: 24px 26px 18px;
  border-bottom: 1px solid #eef2f7;
  h3 { margin: 0; font-size: 17px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
  p { margin: 7px 0 0; font-size: 12.5px; color: #64748b; line-height: 1.55; }
`;

export const SummaryBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 6px 26px 12px;
`;

export const SummaryGroupLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 16px 0 2px;
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: ${(p) => (p.$top ? "flex-start" : "center")};
  justify-content: space-between;
  gap: 20px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
  .k { font-size: 12.5px; color: #64748b; flex-shrink: 0; padding-top: ${(p) => (p.$top ? "2px" : "0")}; }
  .v { font-size: 13px; font-weight: 600; color: #1e293b; text-align: right; }
`;

export const YearChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: flex-end;
  span {
    font-size: 11.5px;
    font-weight: 600;
    color: #2c6a82;
    background: #eef5f8;
    border: 1px solid #dcebf1;
    padding: 2px 9px;
    border-radius: 999px;
    font-variant-numeric: tabular-nums;
  }
`;

export const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 26px;
  border-top: 1px solid #eef2f7;
  background: #fbfcfd;
`;

export const EditButtonSlot = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

export const AmbitionNote = styled.div`
  font-size: 12px;
  color: #3f88a5;
  margin-top: 7px;
  line-height: 1.45;
`;

export const ConstraintHint = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 7px;
`;

export const ErrorHint = styled.div`
  font-size: 11.5px;
  color: #dc2626;
  font-weight: 500;
  margin-top: 7px;
`;

export const CollectNote = styled.div`
  font-size: 12.5px;
  color: #7a6425;
  line-height: 1.55;
  margin: 6px 0 16px;
  padding: 12px 15px;
  background: #fdf8ea;
  border: 1px solid #f0e2b6;
  border-radius: 9px;
`;

export const TableWrap = styled.div`
  width: 100%;
  margin-top: 8px;
`;

export const CollectTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 14px;
  thead th {
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
    padding: 0 12px 11px;
    border-bottom: 1px solid #e4e9ec;
  }
  thead th:first-child { width: 150px; }
  thead th.num { text-align: right; }
  tbody tr { border-bottom: 1px solid #eef2f4; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover td { background: #fafbfc; }
  td { padding: 11px 12px; vertical-align: middle; }
  td.year { font-weight: 600; color: #1f2933; white-space: nowrap; }
`;

export const YearTag = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 10px;
  font-weight: 700;
  color: #2c6480;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const S3TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 18px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  &::-webkit-scrollbar { height: 9px; }
  &::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 8px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
  &::-webkit-scrollbar-thumb:hover { background: #b3c0cf; }
`;

export const S3MatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  
  thead th {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #64748b;
    padding: 13px 16px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    white-space: nowrap;
  }

  tbody tr { border-bottom: 1px solid #eef2f6; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:nth-child(even) td { background: #fcfdfe; }

  td {
    padding: 9px 16px;
    vertical-align: middle;
    white-space: nowrap;
  }

  .sticky-col-nt {
    position: sticky;
    left: 0;
    background: #fff;
    z-index: 5;
    width: 45px;
    min-width: 45px;
    text-align: center;
  }
  .sticky-col-lt {
    position: sticky;
    left: 45px;
    background: #fff;
    z-index: 5;
    width: 45px;
    min-width: 45px;
    text-align: center;
  }
  .sticky-col-cat {
    position: sticky;
    left: 90px;
    background: #fff;
    z-index: 5;
    width: 220px;
    min-width: 220px;
    box-shadow: 6px 0 8px -6px rgba(15, 23, 42, 0.12);
  }

  th.sticky-col-nt, th.sticky-col-lt, th.sticky-col-cat {
    background: #f8fafc;
    z-index: 6;
  }

  .base-year-col {
    background: #f0f7f9 !important;
    box-shadow: inset 1px 0 0 #d8ebf0, inset -1px 0 0 #d8ebf0;
  }

  tr:hover td { background: #f6f9fb; }
  tr:hover td.sticky-col-nt,
  tr:hover td.sticky-col-lt,
  tr:hover td.sticky-col-cat { background: #f3f6f9; }
  tr:hover td.base-year-col { background: #e6f1f5 !important; }
`;

export const CellField = styled.div`
  position: relative;
  input {
    width: 100%;
    height: 36px;
    padding: 0 46px 0 12px;
    border: 1px solid #dce2e6;
    border-radius: 8px;
    font-size: 14px;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: #1f2933;
    font-family: inherit;
    transition: border-color 110ms ease, box-shadow 110ms ease;
  }
  input:hover:not([readonly]):not(:focus) { border-color: #c3ccd2; }
  input:focus {
    outline: none;
    border-color: #3f88a5;
    box-shadow: 0 0 0 3px rgba(63, 136, 165, 0.15);
  }
  input[readonly] { background: #f8fafc; color: #64748b; }
  input[readonly]:focus { box-shadow: none; border-color: #dce2e6; }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  input[type="number"] { -moz-appearance: textfield; }
  .unit {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #98a1a8;
    pointer-events: none;
  }
`;

export const LockedCell = styled.div`
  height: 36px;
  border-radius: 8px;
  background: #f6f8f9;
  border: 1px dashed #d7dde0;
  display: flex;
  align-items: center;
  padding: 0 12px;
  color: #9aa4ac;
  font-size: 13px;
`;

export const ActualCell = styled.div`
  height: 36px;
  border-radius: 8px;
  background: #eef5f8;
  border: 1px solid #cfe0e8;
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-variant-numeric: tabular-nums;
  cursor: default;
  .lock { color: #9cbccb; font-size: 11px; margin-right: 6px; }
  .amt { margin-left: auto; color: #2c6a82; font-weight: 600; font-size: 14px; }
  .unit { color: #9cbccb; font-size: 12px; margin-left: 4px; }
`;

export const CoverageRow = styled.div`
  display: flex;
  gap: 12px;
  margin: 12px 0 22px;
  flex-wrap: wrap;
`;

export const CoverageCardBox = styled.div`
  flex: 1 1 170px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 11px 14px;
  background: #fff;
`;

export const CoverageCardTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const CoverageCardValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  margin-top: 5px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-variant-numeric: tabular-nums;
`;

export const CoverageCardDesc = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
`;

export const ProgressTrack = styled.div`
  height: 4px;
  background: #eef2f6;
  border-radius: 999px;
  margin-top: 8px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, $pct)}%;
  background: ${({ $pass }) => ($pass ? "#3f8a5c" : "#a0651a")};
  border-radius: 999px;
  transition: width 260ms ease;
`;

export const StatusPill = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $pass }) => ($pass ? "#eaf5ee" : "#faf1e6")};
  color: ${({ $pass }) => ($pass ? "#3f8a5c" : "#a0651a")};
  white-space: nowrap;
`;

export const MaterialityBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: ${({ $material }) => ($material ? "#f0f7f9" : "#f8fafc")};
  border: 1px solid ${({ $material }) => ($material ? "#c7dfe7" : "#e2e8f0")};
  border-radius: 11px;
  padding: 15px 17px;
  margin-bottom: 28px;
`;

export const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #eef2f7; }
  th {
    color: #64748b; font-weight: 600; background: #f8fafc; white-space: nowrap;
    font-size: 11.5px; letter-spacing: 0.02em;
  }
  td { color: #334155; white-space: nowrap; }
  tbody tr:hover td { background: #fafbfc; }
`;

export const CurrentBadge = styled.span`
  display: inline-block;
  padding: 3px 11px;
  border-radius: 999px;
  background: #e6f4ea;
  color: #1e7e42;
  font-size: 11px;
  font-weight: 700;
`;

export const Muted = styled.div`
  padding: 30px 8px;
  font-size: 14px;
  color: #64748b;
`;

export const InlineActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const TextButton = styled.button`
  border: none;
  background: transparent;
  color: #3f88a5;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
  transition: color 110ms ease;
  &:hover:not(:disabled) { color: #2f6f89; text-decoration: underline; }
  &:disabled { color: #94a3b8; cursor: default; }
`;

export const SmallInput = styled(SettingsFormInput)`
  width: 120px;
  padding: 7px 10px;
  font-size: 12px;
  border-radius: 7px;
`;
